import React, { useMemo, useState } from "react";
import { TitleBlock, Note, Actions, PrimaryBtn, TextBtn } from "../../../../auth/v2/pieces";
import { Spinner } from "../../../../ForAthletes/v2/onboarding/onboardingPieces";
import { filterTeams } from "../../../../ForBusiness/Cacheshare/hooks/useCascadeFields";
import { SearchField, TeamPickRow, EmptyBlock } from "../coachOnboardingPieces";
import { errorMessage, planFor, scopeLabel, teamBinding, teamInitials, teamKey, teamMeta, teamTitle } from "../coachOnboardingModel";
import { useTeamOptions } from "../useTeamOptions";

// Long lists render in the page up to this many rows; the search narrows them.
const ROW_CAP = 60;

const plural = (n, one) => `${n} ${n === 1 ? one : `${one}s`}`;
// A skipped league unions several sources (SportRadar, Cerebro, partner
// teams); they read as one alphabetical list, as in Figma, by the names shown.
const byName = (a, b) => teamTitle(a).localeCompare(teamTitle(b));

// 08 · Team. The list is every team in the picked scope; the search filters
// it (the "Any league" scope searches every league by name instead).
const TeamStep = ({ filters, team, onSearch, onPick, onSubmit, onCantFind }) => {
  const plan = useMemo(() => planFor(filters), [filters]);
  // `notice`: the list came back limited (the team feed did not fully
  // answer), said above the teams in CACHE's words.
  const { teams, loading, error: loadError, retry, notice } = useTeamOptions(plan);
  const sorted = useMemo(() => [...teams].sort(byName), [teams]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scope = scopeLabel(filters);
  const nameSearch = !!plan?.anyLeague;
  const needsQuery = nameSearch && filters.teamSearch.trim().length < 2;
  const listed = nameSearch ? sorted : filterTeams(sorted, filters.teamSearch);
  const picked = teamKey(team);
  const bound = !!teamBinding(team).league;

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await onSubmit();
    } catch (e) {
      setError(errorMessage(e, "We could not save your team. Try again."));
      setBusy(false);
    }
  };

  const list = () => {
    if (loading) return <div className="flex justify-center py-6"><Spinner /></div>;
    if (loadError) {
      return (
        <Note tone="critical">
          {loadError}{" "}
          <button type="button" className="underline" onClick={retry}>
            Retry
          </button>
        </Note>
      );
    }
    const rows = listed.slice(0, ROW_CAP);
    // A team picked outside the rows (one of 08a's matches, or one the
    // search hides) stays on top, picked.
    const pinned = team && !rows.some((t) => teamKey(t) === picked) ? team : null;
    if (!rows.length && !pinned) {
      if (needsQuery) return <Note>Type at least two letters to search every league.</Note>;
      return (
        <EmptyBlock
          title="No team matched"
          action={
            <button type="button" onClick={onCantFind} className="flex h-9 items-center rounded-full bg-bm-ink px-4 bm-label-xs text-white">
              Can't find team
            </button>
          }
        >
          Try clearing the league filter, or tell us your team.
        </EmptyBlock>
      );
    }
    return (
      <div className="flex flex-col gap-[10px]">
        {(pinned ? [pinned, ...rows] : rows).map((t) => {
          const name = teamTitle(t);
          return <TeamPickRow key={teamKey(t)} initials={teamInitials(name)} name={name} meta={teamMeta(t, filters)} selected={teamKey(t) === picked} onClick={() => onPick(t)} />;
        })}
        {listed.length > ROW_CAP && <p className="text-center bm-body-s text-bm-tertiary">Showing {ROW_CAP} of {listed.length}. Search to narrow.</p>}
      </div>
    );
  };

  return (
    <>
      <TitleBlock eyebrow="Team" title="Which team do you coach?" />
      <SearchField aria-label="Search teams" placeholder={`Search teams in ${scope}...`} value={filters.teamSearch} onChange={(e) => onSearch(e.target.value)} />
      <div className="flex items-center justify-between gap-4">
        <span className="bm-body-s text-bm-tertiary">{loading ? "Loading teams..." : needsQuery ? "" : `${plural(listed.length, "team")} in ${scope}`}</span>
        {team && (
          <span className="min-w-0 truncate bm-body-s text-bm-muted">
            Selected: <span className="font-semibold text-bm-ink">{teamTitle(team)}</span>
          </span>
        )}
      </div>
      {!loading && !loadError && notice && <div role="status"><Note>{notice}</Note></div>}
      {list()}
      {team && !bound && <Note>We cannot link this team yet. Use Can't find team and an admin will add it.</Note>}
      {error && <Note tone="critical">{error}</Note>}
      <Actions>
        <PrimaryBtn disabled={!team || !bound} loading={busy} onClick={submit}>
          {team ? `Continue with ${teamTitle(team)}` : "Continue"}
        </PrimaryBtn>
        <TextBtn onClick={onCantFind}>Can't find team</TextBtn>
      </Actions>
    </>
  );
};

export default TeamStep;
